// ============================================================================
// Tableau de signes (lot 6.19, moteur v42) — calcul EXACT du tableau attendu et
// vérification case par case de celui que l'élève remplit.
//
//   - Le sujet : un membre comparé à 0 (« (x − 2)(x + 3) ≤ 0 », « (1 − 2x) ÷ x > 0 »).
//     Il se découpe en facteurs : polynômes en x (racines EXACTES, toutes connues),
//     exponentielles (toujours positives), puissances entières, constantes négatives
//     (une constante positive ne change aucun signe : pas de ligne).
//   - Les valeurs de x : les racines de tous les facteurs, sans doublon, dans l'ordre.
//     Une case par intervalle (signe + ou −) et par valeur (0 si le facteur s'annule,
//     vide sinon) ; sur la dernière ligne, une double barre « ‖ » là où un facteur du
//     dénominateur s'annule (valeur interdite).
//   - Une ligne « tableau » de la feuille est UN bout : « ⊞ » suivi d'un JSON
//     (`signTableText`), relu par `parseSignTable`. Elle passe telle quelle par la base
//     (`attempt_scratch_lines.atoms`) et par le rejeu du serveur.
//
// Pur : aucune dépendance à l'app. Les signes d'un intervalle se lisent en un point
// de l'intervalle (le facteur ne s'y annule pas : son signe y est constant).
// ============================================================================

import * as Q from './kernel/exact';
import { evalFloat, rfIsZero, rfSimplify, rfToTerm, toRatFn } from './kernel/normal';
import { canon, parseStatement, sub, symbolsOf, termText, type Term } from './kernel/term';
import { univariateSolutions } from './kernel/witness';
import { shapeOf } from './kernel-poly';
import { formatNumber } from './lexer';

/** Une ligne de la feuille qui commence par ce signe est un tableau de signes. */
export const SIGN_TABLE_MARK = '⊞';
/** Au plus : valeurs de x, lignes (facteurs + l'expression). */
export const SIGN_TABLE_MAX_VALUES = 6;
export const SIGN_TABLE_MAX_ROWS = 6;

export type SignCell = '+' | '−' | '0' | '‖' | '';

/**
 * Tableau de VARIATIONS (lot 6.20, moteur v42 étendu) : la ligne de f, sous les signes de
 * f′. Une case d'intervalle porte une flèche (« ↗ », « ↘ ») ; une case de valeur porte la
 * valeur de f (une écriture : « −1 ÷ exp(1) »), la double barre « ‖ » sous une valeur
 * interdite, ou rien.
 */
export const VAR_ARROWS = ['↗', '↘'] as const;
export interface VarRow {
  label: string;
  /** 2 × valeurs + 1 cases : flèche (ou vide) sur un intervalle ; valeur, « ‖ » ou vide */
  cells: string[];
}

/** Le tableau tel que l'élève l'écrit : les valeurs de x, puis une ligne par facteur. */
export interface SignTable {
  /** valeurs de x écrites (sans −∞ ni +∞), dans l'ordre de la ligne des x */
  values: string[];
  /** lignes : facteurs, puis l'expression (dernière ligne) ; 2 × valeurs + 1 cases */
  rows: { label: string; cells: SignCell[] }[];
  /** tableau de variations (lot 6.20) : la ligne de f, sous la dernière ligne (f′) */
  f?: VarRow;
}

const CELL_OF: Readonly<Record<string, SignCell>> = {
  '+': '+',
  '−': '−',
  '-': '−',
  '0': '0',
  '‖': '‖',
  '.': '',
};

export function isSignTableText(text: string): boolean {
  return text.startsWith(SIGN_TABLE_MARK);
}

/** Écriture d'une ligne « tableau » (un seul bout). */
export function signTableText(t: SignTable): string {
  return (
    SIGN_TABLE_MARK +
    JSON.stringify({
      v: t.values,
      r: t.rows.map((r) => [r.label, r.cells.map((c) => c || '.').join('')]),
      // tableau de variations (lot 6.20) : la clé n'existe que s'il y a une ligne de f
      ...(t.f ? { f: [t.f.label, t.f.cells] } : {}),
    })
  );
}

/** Lit une ligne « tableau » ; null si elle est mal formée. */
export function parseSignTable(text: string): SignTable | null {
  if (!isSignTableText(text)) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(text.slice(SIGN_TABLE_MARK.length));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const { v, r, f } = raw as { v?: unknown; r?: unknown; f?: unknown };
  if (!Array.isArray(v) || !Array.isArray(r)) return null;
  if (v.length > SIGN_TABLE_MAX_VALUES || !r.length || r.length > SIGN_TABLE_MAX_ROWS) return null;
  if (!v.every((s) => typeof s === 'string' && s.trim().length > 0 && s.length <= 40)) return null;
  const n = 2 * v.length + 1;
  const rows: SignTable['rows'] = [];
  for (const row of r) {
    if (!Array.isArray(row) || row.length !== 2) return null;
    const [label, cells] = row as unknown[];
    if (typeof label !== 'string' || !label.trim() || label.length > 80) return null;
    if (typeof cells !== 'string') return null;
    const chars = Array.from(cells);
    if (chars.length !== n || !chars.every((c) => c in CELL_OF)) return null;
    rows.push({ label, cells: chars.map((c) => CELL_OF[c]!) });
  }
  if (f === undefined) return { values: v as string[], rows };
  // la ligne de f d'un tableau de variations (lot 6.20)
  if (!Array.isArray(f) || f.length !== 2) return null;
  const [flabel, fcells] = f as unknown[];
  if (typeof flabel !== 'string' || !flabel.trim() || flabel.length > 80) return null;
  if (!Array.isArray(fcells) || fcells.length !== n) return null;
  const cells: string[] = [];
  for (let i = 0; i < n; i++) {
    const c = fcells[i];
    if (typeof c !== 'string' || c.length > 40) return null;
    if (i % 2 === 0 && c !== '' && !(VAR_ARROWS as readonly string[]).includes(c)) return null;
    cells.push(c.trim());
  }
  return { values: v as string[], rows, f: { label: flabel, cells } };
}

/* ------------------------------ tableau attendu ------------------------------ */

interface Factor {
  term: Term;
  label: string;
  /** facteur du dénominateur */
  den: boolean;
  /** racines exactes (vides pour une exponentielle ou une constante) */
  roots: Term[];
  /** valeur constante (négative) : la ligne est « − » partout */
  constant: boolean;
}

export interface ExpectedSignTable {
  x: string;
  /** le membre étudié, tel qu'il s'écrit */
  subject: string;
  values: Term[];
  valueTexts: string[];
  rows: { label: string; cells: SignCell[]; den: boolean }[];
}

const has = (t: Term, x: string): boolean => symbolsOf(t).has(x);

/** Les nombres décimaux écrits comme l'élève les écrit (« 0,5 » et non « 1 ÷ 2 »). */
function writtenDecimals(t: Term): Term {
  switch (t.k) {
    case 'num': {
      const { n, d } = t.v;
      if (d === 1n) return t;
      let r = d;
      let places = 0;
      while (r % 2n === 0n || r % 5n === 0n) {
        r = r % 2n === 0n ? r / 2n : r / 5n;
        if (++places > 12) return t;
      }
      if (r !== 1n) return t;
      return { k: 'sym', n: formatNumber(Number(n) / Number(d)) };
    }
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(writtenDecimals) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: writtenDecimals(t.a), b: writtenDecimals(t.b) } as Term;
    case 'fn':
      return { ...t, a: writtenDecimals(t.a) };
    default:
      return t;
  }
}

const SUP: Readonly<Record<string, string>> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

/**
 * Écriture d'un facteur ou d'une valeur, comme sur la feuille : produits accolés
 * (« (x − 2)(x + 3) », « −2(x − 1) »), exposants entiers en exposant (« x³ »).
 */
export function signText(t: Term): string {
  return (
    termText(writtenDecimals(t))
      .replace(/\) × \(/g, ')(')
      .replace(/(\^\d+|[²³⁴⁵⁶⁷⁸⁹]) × \(/g, '$1(')
      .replace(/(^|[\s(−])(\d+(?:,\d+)?) × \(/g, '$1$2(')
      // un coefficient négatif écrit avant une lettre (« (−0,2) × t » → « −0,2t », lot 6.20)
      .replace(/\((−\d+(?:,\d+)?)\) × ([a-zα-ω])(?![\w(])/g, '$1$2')
      .replace(/\^(\d+)/g, (_m, d: string) => [...d].map((c) => SUP[c]!).join(''))
  );
}

/** Une valeur de x écrite simplement (« −√2 » et non « −√8 ÷ 2 »). */
function valueText(t: Term): string {
  try {
    return signText(rfToTerm(rfSimplify(toRatFn(t))));
  } catch {
    return signText(canon(t));
  }
}

/** Deux valeurs exactes égales ? (forme normale, sinon valeur approchée) */
export function sameSignValue(a: Term, b: Term): boolean {
  try {
    if (rfIsZero(toRatFn(sub(a, b)))) return true;
  } catch {
    /* forme non exacte : on compare les valeurs */
  }
  const fa = evalFloat(a, {});
  const fb = evalFloat(b, {});
  return Number.isFinite(fa) && Number.isFinite(fb) && Math.abs(fa - fb) < 1e-12;
}

/** Racines exactes de f (polynôme en x) ; null si inconnues ou si f n'est pas un polynôme. */
function polyRoots(f: Term, x: string): Term[] | null {
  const shape = shapeOf(f, x);
  if (!shape || shape.opaque || shape.den) return null;
  let sol;
  try {
    sol = univariateSolutions(toRatFn(f), x);
  } catch {
    return null;
  }
  if (!sol || !sol.complete) return null;
  return sol.roots;
}

/** Signe d'un nombre sans x (+1, −1) ; 0 s'il est nul, null s'il ne se calcule pas. */
function constSign(t: Term): number | null {
  const v = evalFloat(t, {});
  if (!Number.isFinite(v)) return null;
  if (Math.abs(v) < 1e-12) return 0;
  return v > 0 ? 1 : -1;
}

/** Découpe en facteurs ; false si un morceau n'a pas de signe connu. */
function collect(t: Term, x: string, den: boolean, out: Factor[]): boolean {
  if (!has(t, x)) {
    const s = constSign(t);
    if (s === null || s === 0) return false;
    if (s < 0)
      out.push({ term: canon(t), label: signText(canon(t)), den, roots: [], constant: true });
    return true;
  }
  switch (t.k) {
    case 'mul':
      return t.c.every((c) => collect(c, x, den, out));
    case 'div':
      return collect(t.a, x, den, out) && collect(t.b, x, !den, out);
    case 'pow': {
      const e = t.b;
      if (e.k !== 'num' || !Q.isInteger(e.v) || e.v.n < 1n || has(e, x)) return false;
      if (t.a.k === 'fn' && t.a.f === 'exp') {
        out.push({ term: t, label: signText(t), den, roots: [], constant: false });
        return true;
      }
      const roots = polyRoots(t.a, x);
      if (!roots) return false;
      out.push({ term: t, label: signText(t), den, roots, constant: false });
      return true;
    }
    case 'fn':
      if (t.f !== 'exp') return false;
      // e^(…) : toujours strictement positive
      out.push({ term: t, label: signText(t), den, roots: [], constant: false });
      return true;
    default: {
      const roots = polyRoots(t, x);
      if (!roots) return false;
      out.push({ term: t, label: signText(t), den, roots, constant: false });
      return true;
    }
  }
}

/**
 * Le tableau de signes attendu pour le membre `side` (comparé à 0), en l'inconnue x ;
 * null si un facteur n'a pas de signe connu, ou si le tableau serait trop grand.
 */
export function expectedSignTable(side: Term, x: string): ExpectedSignTable | null {
  if (!has(side, x)) return null;
  const factors: Factor[] = [];
  if (!collect(side, x, false, factors)) return null;
  if (!factors.some((f) => !f.constant)) return null;
  // valeurs de x : les racines de tous les facteurs, sans doublon, triées
  const vals: { t: Term; f: number }[] = [];
  for (const f of factors)
    for (const r of f.roots) {
      const fv = evalFloat(r, {});
      if (!Number.isFinite(fv)) return null;
      if (!vals.some((v) => sameSignValue(v.t, r))) vals.push({ t: r, f: fv });
    }
  vals.sort((a, b) => a.f - b.f);
  if (vals.length > SIGN_TABLE_MAX_VALUES) return null;
  // un point dans chaque intervalle
  const pts: number[] = [];
  if (!vals.length) pts.push(0);
  else {
    pts.push(vals[0]!.f - 1);
    for (let i = 0; i + 1 < vals.length; i++) pts.push((vals[i]!.f + vals[i + 1]!.f) / 2);
    pts.push(vals[vals.length - 1]!.f + 1);
  }
  const signAt = (t: Term, p: number): SignCell | null => {
    const v = evalFloat(t, { [x]: p });
    if (!Number.isFinite(v) || Math.abs(v) < 1e-12) return null;
    return v > 0 ? '+' : '−';
  };
  const isRoot = (f: Factor, v: Term): boolean => f.roots.some((r) => sameSignValue(r, v));
  // une ligne par facteur (le membre seul, s'il n'a qu'un facteur : sa ligne est la dernière)
  const lone = factors.length === 1;
  const rows: ExpectedSignTable['rows'] = [];
  if (!lone)
    for (const f of factors) {
      const cells: SignCell[] = [];
      for (let i = 0; i < pts.length; i++) {
        const s = f.constant ? '−' : signAt(f.term, pts[i]!);
        if (!s) return null;
        cells.push(s);
        if (i < vals.length) cells.push(isRoot(f, vals[i]!.t) ? '0' : '');
      }
      rows.push({ label: f.label, cells, den: f.den });
    }
  if (rows.length + 1 > SIGN_TABLE_MAX_ROWS) return null;
  // la dernière ligne : le signe du membre, 0 au numérateur, ‖ au dénominateur
  const last: SignCell[] = [];
  for (let i = 0; i < pts.length; i++) {
    const s = signAt(side, pts[i]!);
    if (!s) return null;
    last.push(s);
    if (i < vals.length) {
      const v = vals[i]!.t;
      last.push(
        factors.some((f) => f.den && isRoot(f, v))
          ? '‖'
          : factors.some((f) => !f.den && isRoot(f, v))
            ? '0'
            : '',
      );
    }
  }
  rows.push({ label: signText(side), cells: last, den: false });
  return {
    x,
    subject: signText(side),
    values: vals.map((v) => v.t),
    valueTexts: vals.map((v) => valueText(v.t)),
    rows,
  };
}

/** Les nombres écrits dans un terme (pour proposer de fausses valeurs : « 3 » pour x + 3). */
function numbersIn(t: Term, out: Term[] = []): Term[] {
  switch (t.k) {
    case 'num':
      out.push(t);
      return out;
    case 'add':
    case 'mul':
      t.c.forEach((c) => numbersIn(c, out));
      return out;
    case 'div':
    case 'pow':
    case 'binom':
      numbersIn(t.a, out);
      if (t.k !== 'pow') numbersIn(t.b, out);
      return out;
    case 'fn':
      return numbersIn(t.a, out);
    default:
      return out;
  }
}

/**
 * Les valeurs de x à proposer comme bouts : les racines, et les nombres écrits dans
 * le membre (l'erreur classique : « 3 » au lieu de « −3 » pour x + 3). Dans un ordre
 * qui ne trahit pas l'ordre croissant (par écriture).
 */
export function signTableCandidates(side: Term, exp: ExpectedSignTable, max = 8): string[] {
  const out: { t: Term; text: string }[] = [];
  const push = (t: Term): void => {
    if (out.length >= max) return;
    const v = evalFloat(t, {});
    if (!Number.isFinite(v)) return;
    if (out.some((o) => sameSignValue(o.t, t))) return;
    out.push({ t, text: valueText(t) });
  };
  exp.values.forEach(push);
  numbersIn(side).forEach((n) => {
    if (n.k !== 'num' || Q.isZero(n.v) || Q.eq(n.v, Q.ONE) || Q.eq(n.v, Q.rat(-1))) return;
    push(n);
    push({ k: 'num', v: Q.neg(n.v) });
  });
  return out.map((o) => o.text).sort((a, b) => a.localeCompare(b, 'fr'));
}

/* ------------------------------ vérification ------------------------------ */

export interface SignTableIssue {
  title: string;
  message: string;
  /** case fautive : ligne (0 = première ligne de facteurs) et colonne ; -1 : ligne des x */
  row?: number;
  col?: number;
}

function intervalText(exp: ExpectedSignTable, i: number): string {
  const v = exp.valueTexts;
  const lo = i === 0 ? '−∞' : v[i - 1]!;
  const hi = i === v.length ? '+∞' : v[i]!;
  return `]${lo} ; ${hi}[`;
}

function readValue(text: string): Term | null {
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'expr') return null;
  return Number.isFinite(evalFloat(p.s.e, {})) ? p.s.e : null;
}

/** Les étiquettes du tableau sont-elles celles du tableau attendu (même ordre) ? */
function sameLabels(exp: ExpectedSignTable, t: SignTable): boolean {
  if (t.rows.length !== exp.rows.length) return false;
  return t.rows.every((r, i) => norm(r.label) === norm(exp.rows[i]!.label));
}
const norm = (s: string): string => s.replace(/\s+/g, '').replace(/-/g, '−');

/** La première erreur du tableau de l'élève ; null s'il est juste. */
export function checkSignTable(exp: ExpectedSignTable, t: SignTable): SignTableIssue | null {
  const x = exp.x;
  if (!sameLabels(exp, t))
    return {
      title: 'Ce tableau ne va pas avec ta ligne',
      message: `Les lignes du tableau sont les facteurs de ta dernière ligne comparée à 0, puis ${exp.subject}. Ouvre le tableau depuis la palette.`,
    };
  // 1. la ligne des x
  const got: Term[] = [];
  for (let i = 0; i < t.values.length; i++) {
    const v = readValue(t.values[i]!);
    if (!v)
      return {
        title: 'Valeur illisible',
        message: `Je ne lis pas « ${t.values[i]} » comme un nombre.`,
        row: -1,
        col: i,
      };
    if (!exp.values.some((e) => sameSignValue(e, v)))
      return {
        title: 'Une valeur en trop',
        message: `Aucun facteur ne s’annule en ${x} = ${t.values[i]} : retire cette valeur.`,
        row: -1,
        col: i,
      };
    if (got.some((g) => sameSignValue(g, v)))
      return {
        title: 'Une valeur deux fois',
        message: `${x} = ${t.values[i]} est déjà sur la ligne des ${x}.`,
        row: -1,
        col: i,
      };
    got.push(v);
  }
  if (got.length < exp.values.length)
    return {
      title: 'Il manque une valeur',
      message: `Chaque facteur s’annule pour une valeur de ${x} : cherche-les toutes et place-les sur la ligne des ${x}.`,
      row: -1,
    };
  for (let i = 0; i < got.length; i++)
    if (!sameSignValue(got[i]!, exp.values[i]!))
      return {
        title: 'Dans l’ordre croissant',
        message: `Range les valeurs de ${x} de la plus petite à la plus grande, de −∞ à +∞.`,
        row: -1,
        col: i,
      };
  // 2. les cases, ligne par ligne
  const lastRow = exp.rows.length - 1;
  for (let r = 0; r < exp.rows.length; r++) {
    const e = exp.rows[r]!;
    const g = t.rows[r]!;
    const label = e.label;
    for (let c = 0; c < e.cells.length; c++) {
      const want = e.cells[c]!;
      const have = g.cells[c]!;
      if (want === have) continue;
      const interval = c % 2 === 0;
      const i = c >> 1;
      if (interval) {
        if (!have)
          return {
            title: 'Il manque un signe',
            message: `Mets un signe (+ ou −) sur chaque intervalle : il en manque un sur ${intervalText(exp, i)}.`,
            row: r,
            col: c,
          };
        return {
          title: 'Pas le bon signe',
          message:
            r === lastRow && lastRow > 0
              ? `Sur ${intervalText(exp, i)}, applique la règle des signes aux facteurs de la colonne.`
              : `Le signe de ${label} sur ${intervalText(exp, i)} n’est pas le bon : remplace ${x} par un nombre de cet intervalle.`,
          row: r,
          col: c,
        };
      }
      const v = exp.valueTexts[i]!;
      if (have === '‖' && r !== lastRow)
        return {
          title: 'La double barre',
          message:
            'La double barre ne va que sur la dernière ligne, sous une valeur interdite (le dénominateur s’annule). Sur la ligne d’un facteur, écris 0 là où il s’annule.',
          row: r,
          col: c,
        };
      if (want === '‖')
        return {
          title: 'Une valeur interdite',
          message: `En ${x} = ${v}, le dénominateur s’annule : la valeur est interdite, mets une double barre ‖.`,
          row: r,
          col: c,
        };
      if (want === '0')
        return {
          title: 'Ici, ça s’annule',
          message:
            r === lastRow
              ? `En ${x} = ${v}, un facteur du numérateur s’annule : l’expression vaut 0.`
              : `${label} s’annule en ${x} = ${v} : écris 0.`,
          row: r,
          col: c,
        };
      return {
        title: 'Rien ne s’annule ici',
        message:
          r === lastRow
            ? `En ${x} = ${v}, rien ne s’annule : laisse la case vide.`
            : `${label} ne s’annule pas en ${x} = ${v} : laisse la case vide.`,
        row: r,
        col: c,
      };
    }
  }
  return null;
}

/** Le tableau juste, écrit comme une ligne de la feuille (corrigé, tests). */
export function expectedSignTableText(exp: ExpectedSignTable): string {
  return signTableText({
    values: exp.valueTexts,
    rows: exp.rows.map((r) => ({ label: r.label, cells: r.cells })),
  });
}
